<template>
  <div class="form-wrap">
    <div class="header" :class="{ 'back-action': backButton }">
      <div v-if="backButton" class="back-icon" @click="$emit('prev-step')">
        <IconArrow class="icon-arrow" />
      </div>
      <h2
        v-if="
          [
            '/',
            '/career-track-certificate/',
            '/course-certificate/',
            '/certificate/',
            '/upcoming-courses/',
            '/free-data-science-month/',
            '/start-your-journey-for-free/'
          ].includes($route.path)
        "
        class="h4"
      >
        {{ title }}
      </h2>
      <h1 v-else class="h4">
        {{ title }}
      </h1>
    </div>
    <div v-if="!hideSocials" class="social-login-buttons">
      <div class="google-btn">
        <GoogleLoginButton
          :form-type="formType" :errored="termsError" :terms-agreed="signup.termsAgreed"
          :resources="resources"
          :data-event="tracking === 'signup' ? 'google.click' : 'sign_up_form_google.click'"
          data-async-event
          data-direct-send
          @check="checkTerms"
          v-on="$listeners"
        />
      </div>
      <div>
        <FacebookLoginButton
          :form-type="formType"
          :errored="termsError"
          :terms-agreed="signup.termsAgreed"
          :resources="resources"
          :keep-me-logged-in="!!keepMeLoggedIn"
          :data-event="tracking === 'signup' ? 'facebook.click' : 'sign_up_form_facebook.click'"
          data-async-event
          data-direct-send
          @check="checkTerms"
          v-on="$listeners"
        />
      </div>
      <div>
        <LinkedinLoginButton
          :form-type="formType"
          :errored="termsError"
          :terms-agreed="signup.termsAgreed"
          :resources="resources"
          :keep-me-logged-in="!!keepMeLoggedIn"
          :data-event="tracking === 'signup' ? 'linkedin.click' : 'sign_up_form_linkedin.click'"
          data-async-event
          data-direct-send
          @check="checkTerms"
          v-on="$listeners"
        />
      </div>
    </div>
    <small v-if="$store.state.auth.loginAlert" class="alert-error">Please use your company email address to log in to your business account.</small>
    <div v-if="!hideSocials" class="divider" />
    <form
      class="signup-form"
      autocomplete="chrome-off"
      @submit.prevent="signupUser"
    >
      <div class="input-group" :class="{ invalid: nameAlert }">
        <input
          :id="`name${uniqueId}`" v-model="signup.name"
          type="text"
          name="name"
          autocomplete="name"
          placeholder="Your name"
          :class="{ 'invalid-field': nameAlert }"
          :data-event="tracking === 'signup' ? 'name.click' : ''"
          data-direct-send
          @input="nameAlert = ''"
        >
        <small v-if="nameAlert" class="alert">{{ nameAlert }}</small>
      </div>
      <div class="input-group" :class="{ invalid: emailAlert }">
        <input
          :id="`signup-email${uniqueId}`"
          v-model="signup.email"
          type="text"
          name="signup-email"
          autocomplete="email"
          placeholder="Email address"
          :class="{ 'invalid-field': emailAlert }"
          :data-event="tracking === 'signup' ? 'email.click' : ''"
          data-direct-send
          @input="emailAlert = ''"
        >
        <small v-if="emailAlert" class="alert">{{ emailAlert }}</small>
      </div>
      <div class="input-group" :class="{ invalid: passwordAlert || warningText }">
        <input
          :id="`password${uniqueId}`"
          v-model="signup.password"
          :type="visiblePassword ? 'text' : 'password'"
          name="password"
          autocomplete="current-password"
          placeholder="Password"
          :class="{ password: !visiblePassword, 'invalid-field': passwordAlert }"
          :data-event="tracking === 'signup' ? 'password.click' : ''"
          data-direct-send
          @input="passwordAlert = ''"
          @keyup="validatePass"
        >
        <Component
          :is="visiblePassword ? 'IconEye' : 'IconEyeClosed'"
          class="icon-eye"
          @click="visiblePassword = !visiblePassword"
        />
        <small v-if="warningText" :class="warning" class="alert">{{ warningText }}</small>
      </div>
      <VueRecaptcha
        ref="recaptcha"
        class="recaptcha"
        size="invisible"
        :sitekey="$config.reCaptchaSiteKey"
        :load-recaptcha-script="true"
        @verify="signupUser"
        @expired="onRecaptchaExpired"
      >
        <button
          ref="signup_button"
          class="btn-lg submit h6"
          :class="buttonClass"
          type="submit"
          :disabled="disableButtons"
          :data-event="tracking === 'signup' ? 'signup_button.click' : 'sign_up_form_start_your_career.click'"
          :data-async-event="tracking === 'signup' ? 'signup_button.click' : 'sign_up_form_start_your_career.click'"
          data-direct-send
        >
          {{ buttonText }}
        </button>
      </VueRecaptcha>
      <div v-if="!hideSocials" class="input-group">
        <div :class="{ error: termsError }">
          <input
            :id="`termssignup${uniqueId}`" v-model="signup.termsAgreed" type="checkbox"
            class="custom-checkbox reverse-colors"
            :data-event="tracking === 'signup' ? 'policy.click' : ''"
            data-direct-send
            @change="termsError = false"
          >
          <label :for="`termssignup${uniqueId}`">
            I agree with {{ $config.platformName }}’s
            <a target="_blank" :href="`${$config.siteUrl}/terms-of-use/`">Terms of Use</a>
            and
            <a target="_blank" :href="`${$config.siteUrl}/privacy-policy/`">Privacy Policy</a>
          </label>
        </div>
        <small v-if="termsError" class="alert alert-privacy">Please accept the Privacy Policy to sign up</small>
      </div>
      <p class="or-sign-up">
        Already have an account?
        <span v-if="resources" @click="$emit('login')">
          Log In
        </span>
        <nuxt-link v-else to="/login/">
          Log In
        </nuxt-link>
      </p>
    </form>
  </div>
</template>

<script>
import IconArrow from '@itbpbg/lms-components/src/assets/svg/icon-arrow-back.svg?vue-component';
import VueRecaptcha from 'vue-recaptcha';
import FacebookLoginButton from './FacebookLoginButton';
import GoogleLoginButton from './GoogleLoginButton';
import LinkedinLoginButton from './LinkedinLoginButton';
import IconEye from '../assets/svg/eye.svg?vue-component';
import IconEyeClosed from '../assets/svg/eye-closed.svg?vue-component';

export default {
  components: {
    VueRecaptcha,
    FacebookLoginButton,
    GoogleLoginButton,
    LinkedinLoginButton,
    IconEye,
    IconEyeClosed,
    IconArrow,
  },
  props: {
    title: { type: String, default: 'Create Your Free Account' },
    buttonText: { type: String, default: 'Sign Up' },
    buttonClass: { type: String, default: 'btn-primary' },
    type: { type: String, default: '' },
    uniqueId: { type: String, default: '' },
    tracking: { type: String, default: '' },
    campaign: Boolean,
    hideSocials: Boolean,
    backButton: Boolean,
    resources: Boolean,
  },
  data() {
    return {
      confirmation: false,
      disableButtons: true,
      visiblePassword: false,
      signup: {
        email: '',
        name: '',
        password: '',
        termsAgreed: false,
      },
      warning: '',
      warningText: '',
      keepMeLoggedIn: false,
      termsError: false,
      formType: 'signup',
      nameAlert: '',
      emailAlert: '',
      passwordAlert: '',
    };
  },
  async created() {
    this.$root.$on('check-terms', () => {
      this.checkTerms();
    });
  },
  mounted() {
    if (this.$route.query.error && this.$route.query.error === 'business') {
      this.$store.commit('auth/setLoginAlert', true);
      const query = { ...this.$route.query };
      delete query.error;
      this.$router.replace({ query });
    }
    this.url = this.$route.params.url;
    this.disableButtons = false;
  },
  methods: {
    validatePass() {
      if (this.signup.password !== '') {
        // If the password length is less than or equal to 6
        if (this.signup.password.length < 6) {
          this.warning = 'short';
          this.warningText = 'Too short';
          this.passwordAlert = true;
        }
        // If the password length is greater than 6 and contain any lowercase alphabet or any number or any special character
        if (
          this.signup.password.length >= 6
          && (this.signup.password.match(/[a-z]/)
            || this.signup.password.match(/\d+/)
            || this.signup.password.match(/.[!,@,#,$,%,^,&,*,?,_,~,-,(,)]/))
        ) {
          this.warning = 'weak';
          this.warningText = 'Weak password';
          this.passwordAlert = false;
        }
        // If the password length is greater than 6 and contain alphabet,number,special character respectively
        if (
          this.signup.password.length >= 6
          && ((this.signup.password.match(/[a-z]/) && this.signup.password.match(/\d+/))
            || (this.signup.password.match(/\d+/)
              && this.signup.password.match(/.[!,@,#,$,%,^,&,*,?,_,~,-,(,)]/))
            || (this.signup.password.match(/[a-z]/)
              && this.signup.password.match(/.[!,@,#,$,%,^,&,*,?,_,~,-,(,)]/)))
        ) {
          this.warning = 'good';
          this.warningText = 'Good password';
          this.passwordAlert = false;
        }
        // If the password length is greater than 6 and must contain alphabets,numbers and special characters
        if (
          this.signup.password.length >= 6
          && this.signup.password.match(/[a-z]/)
          && this.signup.password.match(/\d+/)
          && this.signup.password.match(/.[!,@,#,$,%,^,&,*,?,_,~,-,(,)]/)
        ) {
          this.warning = 'strong';
          this.warningText = 'Strong password';
          this.passwordAlert = false;
        }
        // If the password length is greater than 100
        if (this.signup.password.length > 100) {
          this.warning = 'long';
          this.warningText = 'Too long';
          this.passwordAlert = true;
        }
      } else {
        this.warning = '';
        this.warningText = '';
        this.passwordAlert = true;
      }
    },
    checkTerms() {
      if (!this.signup.termsAgreed && !this.hideSocials) {
        this.termsError = true;

        return this.termsError;
      }
      this.termsError = false;
      return this.termsError;
    },
    async signupUser() {
      this.disableButtons = true;
      const { event } = this.$refs.signup_button.dataset;

      try {
        const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        if (!this.signup.name) {
          this.nameAlert = 'Required field';
          this.resetForm();
          this.$tracking.sendAsyncEvent(event, { status: 'fail', error: 'Please fill in name!' });
          return;
        }
        if (!this.signup.email || !re.test(this.signup.email)) {
          this.emailAlert = 'Invalid email';
          this.resetForm();
          this.$tracking.sendAsyncEvent(event, { status: 'fail', error: 'Please fill in valid email!' });
          return;
        }
        this.validatePass();
        if (this.passwordAlert) {
          if (this.warning === 'long') {
            this.warningText = 'Maximum password length is 100 symbols';
            this.$tracking.sendAsyncEvent(event, { status: 'fail', error: 'Maximum password length is 100 symbols' });
          } else {
            this.warning = 'short';
            this.warningText = 'Minimum password length is 6 symbols ';
            this.$tracking.sendAsyncEvent(event, { status: 'fail', error: 'Minimum password length is 6 symbols' });
          }
          this.resetForm();
          return;
        }

        if (!this.signup.termsAgreed && !this.hideSocials) {
          this.termsError = true;
          this.resetForm();
          return;
        }
        let signup;
        if (this.type === 'gateway') {
          signup = await this.$axios.$post('/auth/beta/sign-up', {
            email: this.signup.email,
            name: this.signup.name,
          });
        } else {
          signup = await this.$store.dispatch('auth/register', {
            email: this.signup.email,
            name: this.signup.name,
            password: this.signup.password,
            campaign: this.campaign,
            resources: this.resources,
          });
        }
        if (signup) {
          if (signup.status !== 'error') {
            const eventID = `${this.$store.state.tracking.sessionId}${Date.now()}`;
            dataLayer.push({ event: 'FreeSignup', signupType: 'Direct', eventID });
            this.$tracking.sendEvent({
              dataLayerEvent: 'SignUp',
              destinationUrl: this.$router.currentRoute.fullPath,
              eventID,
              capi: true,
              userAgent: navigator.userAgent,
            });
            await this.$tracking.sendAsyncEvent(event, { status: 'success' });
            if (this.resources) {
              this.$emit('continueToExamResult');
              return;
            }
            this.$redirectAfterLogin('/');
            return;
          }
          this.$tracking.sendAsyncEvent(event, { status: 'fail', error: signup.message });
        }
        this.resetForm();
      } catch (error) {
        this.resetForm();
      }
      this.resetForm();
    },
    onRecaptchaExpired() {
      this.resetForm();
    },
    resetForm() {
      this.disableButtons = false;
      this.$refs.recaptcha.reset();
    },
  },
};
</script>

<style lang="scss" scoped>
.alert-error {
  position: static;
  text-align: center;
  display: block;
  margin-top: var(--spacing-8);
  height: auto;
  background-color: var(--main-red-light-2);
  border: 1px solid var(--main-red-light-3);
  border-radius: 5px;
  color: var(--main-red-shade-1);
  font-weight: 500;
  font-size: 14px;
  line-height: 130%;
  padding: 12px 30px;
}

.form-wrap {
  background-color: var(--main-light);
  border-radius: 10px;
  padding: var(--spacing-48);
  max-width: 446px;
  width: 100%;

  .header {
    &.back-action {
      position: relative;
      padding-left: var(--spacing-8);

      .back-icon {
        position: absolute;
        left: -6px;
        top: 50%;
        padding: var(--spacing-6);
        transform: translateY(-50%);
        cursor: pointer;
      }
    }
  }

  h1,
  h2 {
    margin-top: 0;
    text-align: center;
    margin-bottom: var(--spacing-32);
  }

  h1 {
    margin-bottom: var(--spacing-24);
  }

  .divider {
    position: relative;
    padding: var(--spacing-36) 0;

    &:before,
    &:after {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
    }

    &:before {
      background-color: var(--main-light);
      color: var(--main-dark-opacity-48);
      content: 'or';
      font-size: 16px;
      padding: var(--spacing-12);
      z-index: 2;
    }

    &:after {
      border-top: 1px solid var(--main-dark-opacity-18);
      content: '';
      width: 100%;
    }
  }

  .input-group {
    position: relative;
    padding-bottom: 12px;

    &.invalid {
      padding-bottom: var(--spacing-20);
    }

    &:last-of-type .alert {
      padding-left: var(--spacing-26);
    }

    &.links {
      display: flex;
      justify-content: space-between;
      font-size: 14px;
    }

    input.invalid-field {
      border-color: var(--main-red);
    }

    .alert {
      position: absolute;
      bottom: 0;
      padding-left: 0;
      color: var(--main-red);

      &.alert-privacy {
        position: static;
        text-align: center;
        display: block;
        margin-top: var(--spacing-8);
        line-height: 130%;
        padding: var(--spacing-12);
        background-color: var(--main-red-light-2);
        border: 1px solid var(--main-red-light-3);
        border-radius: 5px;
        color: var(--main-red-shade-1);
      }
    }

    .icon-eye {
      cursor: pointer;
      position: absolute;
      right: var(--spacing-16);
      top: calc(50% - 21px);
    }

    small {
      color: var(--main-red);
      padding-left: 26px;
    }
  }

  small {
    &.short,
    &.weak,
    &.error {
      color: var(--main-red);
    }

    &.good {
      color: var(--accent) !important;
    }

    &.strong {
      color: var(--main-green-shade-1) !important;
    }
  }

  .submit {
    font-weight: 600;
    margin: var(--spacing-12) auto var(--spacing-16);
    width: 100%;
  }

  .or-sign-up {
    color: var(--main-dark-opacity-48);
    text-align: center;
    margin: var(--spacing-12) 0 0;
    font-size: 16px;
    line-height: 150%;

    span {
      color: var(--link-color);
      cursor: pointer;
    }
  }

  @include bp($bp-mobile) {
    padding: var(--spacing-24);

    h1 {
      margin-bottom: var(--spacing-36);
    }

    .or-sign-up {
      margin-top: var(--spacing-36);
    }

    &.upcoming {
      .divider:before {
        background-color: var(--main-light-shade-1);
      }
    }
  }
}

.social-login-buttons {
  display: block;
  overflow: hidden;

  > div {
    border-radius: 5px;
    border: 1px solid var(--main-dark-opacity-12);
    float: left;
    height: 52px;
    width: 52px;

    &:hover {
      background-color: var(--main-light-shade-1);
      border: 1px solid var(--main-dark-opacity-12);
    }

    &:not(:last-of-type) {
      margin-right: 12px;
    }

    &.google-btn {
      width: calc(100% - 128px);
    }
  }
}

.fa {
  @include bp($bp-mobile) {
    .form-wrap {
      &.upcoming {
        .divider:before {
          background-color: var(--main-light);
        }
      }
    }
  }
}
</style>
